import type { SolutionPageContent } from '../../types/solution'

export const hospitalitySolution: SolutionPageContent = {
  slug: 'hospitality',
  hero: {
    headline: 'Fill your rooms and know what every night earns',
    subheadline: 'Track bookings, guest bills, and property income in one place, from the front desk or your phone.',
    card: {
      type: 'progress',
      title: 'Tonight · Kinigi Hills',
      badge: { label: '83% full', tone: 'positive' },
      rows: [
        { label: 'Rooms occupied', value: '10 of 12', pct: 83, tone: 'positive' },
        { label: 'Check-ins today', value: '4', pct: 33, tone: 'brand' },
        { label: 'Unpaid bills', value: 'RWF 186,000', pct: 20, tone: 'warning' },
      ],
    },
  },
  challenges: [
    {
      quote: 'Guests leave and I realise the bar bill was never added.',
      detail: 'Room service, drinks, and laundry go on paper and get forgotten.',
    },
    {
      quote: 'I can’t see bookings and payments in one place.',
      detail: 'Some guests paid a deposit, some paid in full, and I’m never sure who.',
    },
    {
      quote: 'I don’t know if my property is actually profitable.',
      detail: 'Electricity, cleaning, and repairs eat into income I can’t see clearly.',
    },
  ],
  helps: [
    {
      solves: 'The bar bill was never added',
      title: 'One bill per guest, from arrival to checkout',
      copy: 'Add room nights, meals, drinks, and laundry to the guest’s bill as they happen. Settle everything at checkout with an EBM receipt.',
      product: 'sales-pos',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Room 7 · J. Mugisha',
          meta: '3 nights',
          rows: [
            { label: 'Room (3 nights)', value: 'RWF 150,000' },
            { label: 'Dinner × 2', value: 'RWF 24,000' },
            { label: 'Bar', value: 'RWF 11,500' },
            { label: 'Laundry', value: 'RWF 4,000' },
          ],
          footer: { label: 'Balance due', value: 'RWF 139,500' },
          action: 'Check out',
        },
      },
    },
    {
      solves: 'I can’t see bookings and payments',
      title: 'Guests, deposits, and balances',
      copy: 'Record each booking with its deposit, and see exactly what every guest still owes before they arrive.',
      product: 'customers',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Arrivals this week',
          rows: [
            { label: 'Mugisha family', sublabel: 'Mon · 2 rooms', value: 'Deposit paid', tone: 'positive' },
            { label: 'Rwanda Tours Ltd', sublabel: 'Wed · 4 rooms', value: 'RWF 400,000 due', tone: 'warning' },
            { label: 'Ingrid N.', sublabel: 'Fri · 1 room', value: 'Paid in full', tone: 'positive' },
          ],
        },
      },
    },
    {
      solves: 'I don’t know if my property is profitable',
      title: 'Income and costs by property',
      copy: 'Record running costs as they happen and see profit for each property or apartment block, month by month.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'bars',
          title: 'Monthly profit · Kinigi Hills',
          value: 'RWF 3.1M',
          delta: { label: '+9%', tone: 'positive' },
          bars: [
            { label: 'Apr', value: 2.2 },
            { label: 'May', value: 2.4 },
            { label: 'Jun', value: 2.9 },
            { label: 'Jul', value: 3.3 },
            { label: 'Aug', value: 2.8 },
            { label: 'Sep', value: 3.1, highlight: true },
          ],
          legend: 'Income RWF 5.4M · Costs RWF 2.3M',
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'From morning check-outs to evening arrivals.',
    items: [
      { time: '7:00', title: 'Check today’s arrivals', copy: 'See who’s coming and what they’ve paid.' },
      { time: '10:00', title: 'Check guests out', copy: 'Settle the full bill with one receipt.' },
      { time: '12:00', title: 'Record costs', copy: 'Log cleaning supplies, repairs, and utilities.' },
      { time: '15:00', title: 'Welcome new guests', copy: 'Open a bill and take the balance.' },
      { time: '21:00', title: 'Review the day', copy: 'Occupancy, income, and unpaid bills.' },
    ],
  },
  results: [
    { value: '+12%', label: 'more revenue from extras added to guest bills' },
    { value: '0', label: 'guests leaving with unpaid balances' },
    { value: '1 view', label: 'of income and costs across every property' },
  ],
  testimonial: {
    quote: 'Every drink and meal now lands on the right room. Checkout takes two minutes, and nothing is forgotten.',
    name: 'Patrick Nshimiyimana',
    role: 'Owner',
    business: 'Kinigi Hills Guesthouse',
    location: 'Musanze',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/afacccfd-cb4d-44d3-aec6-9b065b1fefd9.jpg',
    metric: { value: '+12%', label: 'more revenue from room extras' },
  },
  products: ['sales-pos', 'customers', 'expenses', 'reports'],
  plan: { name: 'Growth', audience: 'hospitality businesses' },
  faq: [
    {
      question: 'Can I add restaurant and bar charges to a room?',
      answer: 'Yes. Add meals, drinks, and services to a guest’s bill during their stay and settle everything at checkout.',
    },
    {
      question: 'Can I record deposits for bookings?',
      answer: 'Yes. Record deposits against a booking, and Kayko shows the balance due on arrival.',
    },
    {
      question: 'Is Kayko a full booking engine?',
      answer: 'Kayko tracks guests, bills, and income. Online booking and channel management aren’t included today.',
    },
    {
      question: 'Can I manage several apartments or properties?',
      answer: 'Yes. On the Growth plan, set up each property as a location and see income and costs for each.',
    },
    {
      question: 'Can I invoice companies for group stays?',
      answer: 'Yes. Issue an EBM invoice to a company and track the payment until it’s settled.',
    },
  ],
  related: ['food-beverage', 'manage-multiple-locations', 'sell-and-get-paid'],
  cta: {
    headline: 'Know what every room earns',
    subheadline: 'Set up in minutes. Local support in Kigali when you need it.',
  },
}
